import { createContext, PropsWithChildren } from 'react';
import { HqoClientSdk } from '@hqo/hqo-miniapp-client-sdk';
import { useContextValue } from 'app/shared/hooks/use-context-value';
import { useUiMetadataQuery } from 'app/data/queries/use-ui-metadata-query';

const MiniappSdkContext = createContext<HqoClientSdk | null>(null);

// Module-level so the ONE client survives React 18 StrictMode's render-purity double-invoke
// (a useMemo/useRef would re-run the side-effectful constructor). We track the app_uuid the
// current client was built with so we can tell an empty-uuid provisional client apart from a
// real one.
let sdkClient: HqoClientSdk | null = null;
let sdkClientAppUuid = '';

const resolveSdkClient = (settled: boolean, appUuid?: string): HqoClientSdk | null => {
  const nextAppUuid = appUuid || '';

  // Build the client once and keep it for the page lifetime — the SDK constructor calls init()
  // and registers window listeners with NO teardown API, so rebuilding on every app_uuid change
  // would leak the previous instance's listeners.
  //
  // The ONE allowed rebuild is an empty -> real app_uuid upgrade. app_uuid is NOT guaranteed at
  // first settle (the ui-metadata query can be disabled/errored, or a building may expose no
  // metadata), and we still construct with an empty app_uuid so auth/user/building/theme work.
  // But a client frozen with an empty uuid would send `apps//sessions` forever (404 on the
  // app-session call), so if a real app_uuid later arrives we rebuild exactly once to adopt it —
  // the listeners leaked by that single swap are the acceptable cost of an otherwise permanently
  // broken app session. A real -> different-real change is NOT a rebuild (app_uuid is fixed per
  // launch in practice), which keeps the no-teardown leak at bay.
  const canAdoptRealUuid = sdkClient !== null && sdkClientAppUuid === '' && nextAppUuid !== '';
  if (sdkClient && !canAdoptRealUuid) {
    return sdkClient;
  }

  // Build only after ui-metadata settles: building eagerly with an empty app_uuid caused a bogus
  // `apps//sessions` 404 plus a second construction once metadata resolved.
  if (!settled) {
    return sdkClient;
  }

  sdkClient = new HqoClientSdk({ develop: false, app_uuid: nextAppUuid, init_timeout: undefined });
  sdkClientAppUuid = nextAppUuid;

  return sdkClient;
};

// Test-only: reset the module singleton so each test starts from a clean slate.
export const resetMiniappSdkClientForTests = (): void => {
  sdkClient = null;
  sdkClientAppUuid = '';
};

export const MiniappSdkProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const { uiMetadata, isLoading } = useUiMetadataQuery();
  const miniappSdkClient = resolveSdkClient(!isLoading, uiMetadata?.app_uuid);

  return <MiniappSdkContext.Provider value={miniappSdkClient}>{children}</MiniappSdkContext.Provider>;
};

export const useMiniappSdk = (): HqoClientSdk => useContextValue(MiniappSdkContext) as HqoClientSdk;

export { MiniappSdkContext };
